// frontend/src/components/calendar/CalendarDaysView.jsx
//
// Outlook-style month grid. Reads (pattern, holidays, exceptions) from
// React Query and resolves every cell client-side. Month flips are
// instant — no network call.
//
// Click handling:
//   - working day  → opens an inline popover; "Mark as closed" creates
//                    a one-off CalendarException for that date.
//   - exception    → popover with "Remove" + rename.
//   - holiday      → popover linking to the Recurring Holidays section.
//   - weekend      → popover linking to the Working Days toggles.

import React, { useState, useMemo, useRef, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useStoreToday } from '../../hooks/useStoreToday';
import {
  IconButton,
  Typography,
  Button,
  Popover,
  TextField,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { ICON, CaretLeft, CaretRight } from '../../icons';

import {
  useCalendarHolidays,
  useCalendarExceptions,
  useCreateCalendarException,
  useUpdateCalendarException,
  useDeleteCalendarException,
} from '../../hooks/useCalendarData';
import { buildResolver, formatMonthLabel, formatWeekdayShortLabels } from './workingDayResolver';
import { translateRequestError } from '../../queries/api';
import '../../styles/calendar.css';
import { showToast } from '../../components/common/appToastBus';


function pad2(n) { return String(n).padStart(2, '0'); }
function iso(y, m, d) { return `${y}-${pad2(m)}-${pad2(d)}`; }

// This grid marks today on a store's operating calendar (Calendar /
// CalendarException), so it takes the store's day from useStoreToday like
// every other business date, never the browser's.


const CalendarDaysView = ({ calendar, workingDaysPattern, onScrollToHolidays, onScrollToWorkingDays }) => {
  const { t, i18n } = useTranslation();

  // Pattern override: while the user edits the Working Days toggles
  // upstream but hasn't saved yet, we honor the live preview so the
  // grid mirrors what the merchant is about to commit.
  const previewedCalendar = useMemo(() => (
    workingDaysPattern
      ? { ...calendar, working_days_pattern: workingDaysPattern }
      : calendar
  ), [calendar, workingDaysPattern]);

  const calendarId = calendar?.calendar_id;
  const { holidays, initialLoad: holLoading } = useCalendarHolidays(calendarId);
  const { exceptions, initialLoad: excLoading } = useCalendarExceptions(calendarId);

  const createException = useCreateCalendarException(calendarId);
  const updateException = useUpdateCalendarException();
  const deleteException = useDeleteCalendarException();

  const resolver = useMemo(
    () => buildResolver(previewedCalendar, holidays, exceptions),
    [previewedCalendar, holidays, exceptions],
  );

  const storeToday = useStoreToday();

  // Which month is on screen, as an offset from the store's current month
  // rather than a (year, month) pair. Two reasons: the view then follows the
  // store across its own month rollover instead of pinning whatever month it
  // was opened in, and "go to today" is 0 — it cannot disagree with the day
  // the grid highlights.
  const [monthOffset, setMonthOffset] = useState(0);

  const { year, month } = useMemo(() => {
    if (!storeToday) return { year: null, month: null };
    const shifted = new Date(Date.UTC(
      Number(storeToday.slice(0, 4)),
      Number(storeToday.slice(5, 7)) - 1 + monthOffset,
      1,
    ));
    return { year: shifted.getUTCFullYear(), month: shifted.getUTCMonth() + 1 };
  }, [storeToday, monthOffset]);

  const goPrev = () => setMonthOffset(o => o - 1);
  const goNext = () => setMonthOffset(o => o + 1);
  const goToday = () => setMonthOffset(0);

  // Weekday header strip, localized.
  const weekdayHeaders = useMemo(
    () => formatWeekdayShortLabels(i18n.language), [i18n.language],
  );

  // Build a 6-row grid (Outlook-shape: always 6 rows so the height
  // doesn't jump between months). null = padding cell from prev/next
  // month, intentionally not interactive.
  const weeks = useMemo(() => {
    if (!year) return [];
    const firstDow = (new Date(year, month - 1, 1).getDay() + 6) % 7;  // Mon=0
    const daysInMonth = new Date(year, month, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = iso(year, month, d);
      const { kind, label } = resolver.resolve(dateStr);
      cells.push({ day: d, dateStr, kind, label });
    }
    while (cells.length < 42) cells.push(null);
    const out = [];
    for (let i = 0; i < 6; i++) out.push(cells.slice(i * 7, i * 7 + 7));
    return out;
  }, [year, month, resolver]);

  // Popover state for click interactions.
  const [popover, setPopover] = useState(null);   // { anchor, cell, exception, holiday }
  const [editingName, setEditingName] = useState('');
  const inputRef = useRef(null);

  const openCellPopover = (event, cell) => {
    const anchor = event.currentTarget;
    if (cell.kind === 'exception') {
      const exc = resolver.findException(cell.dateStr);
      setEditingName(exc?.exception_name || '');
      setPopover({ anchor, cell, exception: exc });
    } else if (cell.kind === 'holiday') {
      setPopover({ anchor, cell });
    } else if (cell.kind === 'weekend') {
      setPopover({ anchor, cell });
    } else {
      setEditingName('');
      setPopover({ anchor, cell });
    }
  };

  const closePopover = () => setPopover(null);

  const handleAddException = async () => {
    if (!popover) return;
    try {
      await createException.mutateAsync({
        exceptionDate: popover.cell.dateStr,
        exceptionName: editingName,
      });
      closePopover();
    } catch (err) {
      console.error('createCalendarException:', err.message);
      showToast(translateRequestError(err, t, 'msg.saveFailed'), 'error');
    }
  };

  const handleRemoveException = async () => {
    if (!popover?.exception) return;
    try {
      await deleteException.mutateAsync({ id: popover.exception.calendar_exception_id });
      closePopover();
    } catch (err) {
      console.error('deleteCalendarException:', err.message);
      showToast(translateRequestError(err, t, 'msg.saveFailed'), 'error');
    }
  };

  const handleRenameException = async () => {
    if (!popover?.exception) return;
    try {
      await updateException.mutateAsync({
        id: popover.exception.calendar_exception_id,
        exceptionDate: popover.exception.exception_date,
        exceptionName: editingName,
      });
      closePopover();
    } catch (err) {
      console.error('updateCalendarException:', err.message);
      showToast(translateRequestError(err, t, 'msg.saveFailed'), 'error');
    }
  };

  // The store's zone is a precondition for drawing anything here — which month
  // to show and which cell is today both come from it — so it joins the
  // existing spinner rather than being papered over with the browser's day.
  const initialLoad = holLoading || excLoading || !storeToday;
  const todayStr = storeToday;
  const monthLabel = year ? formatMonthLabel(year, month, i18n.language) : '';

  return (
    <div className="calendar-days-view">
      {/* The nav belongs to a rendered calendar: its label names the month on
          screen and its buttons move between months. While `initialLoad` holds
          there is no month on screen, so showing it meant an empty label beside
          buttons that step through nothing. */}
      {!initialLoad && (
        <div className="calendar-month-nav">
          <IconButton onClick={goPrev} size="small" aria-label={t('calendar.prevMonth')}>
            <CaretLeft size={ICON.xl} />
          </IconButton>
          <Typography variant="subtitle1" className="calendar-month-label">
            {monthLabel}
          </Typography>
          <IconButton onClick={goNext} size="small" aria-label={t('calendar.nextMonth')}>
            <CaretRight size={ICON.xl} />
          </IconButton>
          <Button onClick={goToday} size="small" sx={{ ml: 'auto' }}>
            {t('calendar.today')}
          </Button>
        </div>
      )}

      {initialLoad ? (
        <div className="calendar-loading"><CircularProgress size={24} /></div>
      ) : (
        <>
          <div className="calendar-grid-header">
            {weekdayHeaders.map((h) => (
              <div key={h} className="calendar-grid-header-cell">{h}</div>
            ))}
          </div>

          <div className="calendar-grid">
            {weeks.map((week, wi) => (
              <div key={wi} className="calendar-grid-row">
                {week.map((cell, di) => {
                  if (cell === null) {
                    return <div key={di} className="calendar-grid-cell empty" />;
                  }
                  const isToday = cell.dateStr === todayStr;
                  const cls = `calendar-grid-cell kind-${cell.kind}${isToday ? ' today' : ''}`;
                  return (
                    <Tooltip key={di} title={cell.label} describeChild>
                      <button
                        type="button"
                        className={cls}
                        onClick={(e) => openCellPopover(e, cell)}
                      >
                        <span className="calendar-grid-date">{cell.day}</span>
                        {cell.label && (
                          <span className="calendar-grid-name">{cell.label}</span>
                        )}
                      </button>
                    </Tooltip>
                  );
                })}
              </div>
            ))}
          </div>

          <div className="calendar-legend">
            <span className="calendar-legend-item">
              <span className="calendar-legend-dot kind-working" /> {t('calendar.legend.working')}
            </span>
            <span className="calendar-legend-item">
              <span className="calendar-legend-dot kind-weekend" /> {t('calendar.legend.weekend')}
            </span>
            <span className="calendar-legend-item">
              <span className="calendar-legend-dot kind-holiday" /> {t('calendar.legend.holiday')}
            </span>
            <span className="calendar-legend-item">
              <span className="calendar-legend-dot kind-exception" /> {t('calendar.legend.exception')}
            </span>
            <span className="calendar-legend-item">
              <span className="calendar-legend-dot today-marker" /> {t('calendar.legend.today')}
            </span>
          </div>
        </>
      )}

      <Popover
        open={!!popover}
        anchorEl={popover?.anchor || null}
        onClose={closePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <div className="calendar-popover">
          {popover?.cell?.kind === 'working' && (
            <>
              <div className="calendar-popover-title">
                {t('calendar.popover.markClosedTitle', { date: popover.cell.dateStr })}
              </div>
              <TextField
                inputRef={inputRef}
                size="small"
                fullWidth
                autoFocus
                value={editingName}
                onChange={(e) => setEditingName(e.target.value)}
                placeholder={t('calendar.popover.exceptionNamePlaceholder')}
              />
              <div className="calendar-popover-actions">
                <Button onClick={closePopover} size="small">{t('btn.cancel')}</Button>
                <Button
                  onClick={handleAddException}
                  size="small"
                  variant="contained"
                  loading={createException.isPending}
                >
                  {t('calendar.popover.markClosed')}
                </Button>
              </div>
            </>
          )}

          {popover?.cell?.kind === 'exception' && (
            <>
              <div className="calendar-popover-title">
                {t('calendar.popover.editExceptionTitle', { date: popover.cell.dateStr })}
              </div>
              <TextField
                size="small"
                fullWidth
                autoFocus
                value={editingName}
                onChange={(e) => setEditingName(e.target.value)}
                placeholder={t('calendar.popover.exceptionNamePlaceholder')}
              />
              <div className="calendar-popover-actions">
                <Button onClick={closePopover} size="small">{t('btn.cancel')}</Button>
                <Button
                  onClick={handleRemoveException}
                  size="small"
                  color="error"
                  loading={deleteException.isPending}
                >
                  {t('calendar.popover.remove')}
                </Button>
                <Button
                  onClick={handleRenameException}
                  size="small"
                  variant="contained"
                  loading={updateException.isPending}
                >
                  {t('btn.saveChanges')}
                </Button>
              </div>
            </>
          )}

          {popover?.cell?.kind === 'holiday' && (
            <>
              <div className="calendar-popover-title">
                {popover.cell.label || t('calendar.legend.holiday')}
              </div>
              <div className="calendar-popover-hint">
                {t('calendar.popover.holidayHint')}
              </div>
              <div className="calendar-popover-actions">
                <Button onClick={closePopover} size="small">{t('btn.close')}</Button>
                {onScrollToHolidays && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => { onScrollToHolidays(); closePopover(); }}
                  >
                    {t('calendar.popover.goToHolidays')}
                  </Button>
                )}
              </div>
            </>
          )}

          {popover?.cell?.kind === 'weekend' && (
            <>
              <div className="calendar-popover-title">
                {t('calendar.popover.weekendTitle')}
              </div>
              <div className="calendar-popover-hint">
                {t('calendar.popover.weekendHint')}
              </div>
              <div className="calendar-popover-actions">
                <Button onClick={closePopover} size="small">{t('btn.close')}</Button>
                {onScrollToWorkingDays && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => { onScrollToWorkingDays(); closePopover(); }}
                  >
                    {t('calendar.popover.goToWorkingDays')}
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </Popover>
    </div>
  );
};

export default memo(CalendarDaysView);
